import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@Thread-Magic/jasmine';
import sortBy from 'lodash.sortby';
import { useQuery, useMutation } from 'react-query';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { chatgenieAPI } from 'src/config/api';
import queryKeys from 'src/constants/queryKeys';
import useToast from 'src/hooks/useToast';
import useAuthUser from 'src/hooks/useAuthUser';
import PageHolder from 'src/components/PageHolder';
import SimpleTextarea from 'src/components/SimpleTextarea';
import * as Styled from './style';
import CopilotTestChat from '../Copilot/CopilotTestChat';
import { Card } from '../Copilot/CopilotTestChat/style';

const CopilotPrioritization = ({ companyId }) => {
  const authUser = useAuthUser();
  const { toast } = useToast();
  const isParentCompany = () => authUser.company.id === companyId;
  const { data, isLoading, refetch } = useQuery(
    [queryKeys.GET_COPILOT_PRIORITIES],
    chatgenieAPI.getCopilotPriorities,
    {
      onError: error => {
        toast.error(error.message);
      }
    }
  );

  const defaultState = {
    override_priorities: 0,
    priorities: []
  };

  const { control, handleSubmit, reset } = useForm({ defaultValues: defaultState });
  const overridePriorities = useWatch({ control, name: 'override_priorities' });
  const priorities = useWatch({ control, name: 'priorities' });

  const mutation = useMutation(
    ({ priorities }) =>
      chatgenieAPI.updateCopilotPriorities({
        companyId,
        data: priorities.map(({ id, text }) => ({
          id,
          copilot_description: { en: text || '' }
        }))
      }),
    {
      onSuccess: () => {
        refetch();
        toast.success('Successfully saved');
      },
      onError: error => {
        toast.error(error.message);
      }
    }
  );

  const handleSendMessage = ({ message }) => {
    return chatgenieAPI.testCopilotPriorities({
      companyId,
      data: {
        message,
        descriptions: priorities.map(({ name, text }) => ({
          name,
          description: text
        }))
      }
    });
  };

  const newMessageNormalizer = ({ priority, explanation }) => {
    return {
      type: 'copilot',
      name: 'Magic AI',
      body: (
        <Card>
          <h5>Auto prioritize action</h5>
          <div>
            <p>
              <b>{priority}</b>
            </p>
            <p>{explanation}</p>
          </div>
        </Card>
      )
    };
  };

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        priorities: sortBy(data.priorities, [object => object.sort_order])
      });
    }
  }, [data]);

  useEffect(() => {
    mutation.reset();
  }, []);

  if (isLoading) {
    return <PageHolder fullHeight isLoading={isLoading} />;
  }

  return (
    <Styled.Wrapper>
      <Styled.Form
        onSubmit={handleSubmit(mutation.mutate)}
        disabled={!isParentCompany() && !overridePriorities}
      >
        {!isParentCompany() && (
          <Styled.FormGroup>
            <Controller
              control={control}
              name="override_priorities"
              render={({ field: { onChange, ref, value } }) => (
                <Styled.CustomCheckbox
                  id="override_priorities"
                  onChange={onChange}
                  inputRef={ref}
                  name="override_priorities"
                  checked={value}
                >
                  Override parent priorities
                </Styled.CustomCheckbox>
              )}
            />
            <Styled.Separator />
          </Styled.FormGroup>
        )}
        <Styled.Title>Set your priority rules</Styled.Title>
        <Styled.Description>
          In order to better understand which Priority CoPilot should assign to which Thread, we
          need to understand your process. These are synced from your PSA.
          <br />
          <a
            href="https://docs.getthread.com/article/c1vq6iftrh-how-to-set-up-auto-prioritization"
            target="_blank"
            rel="noreferrer"
          >
            Read more about it here
          </a>
        </Styled.Description>
        {priorities.map((priority, index) => (
          <Styled.FormGroup key={priority.id}>
            <Styled.SubTitle>{priority.name}</Styled.SubTitle>
            <Controller
              control={control}
              name={`priorities.${index}`}
              render={({ field: { value, onChange } }) => (
                <SimpleTextarea
                  id={value.id}
                  key={value.id}
                  autoresize
                  name={value.name}
                  value={value.text}
                  placeholder={data.priorities && data.priorities[index]?.text}
                  onChange={event => onChange({ ...value, text: event.target.value })}
                />
              )}
            />
          </Styled.FormGroup>
        ))}
        {mutation.error && <Styled.Error>{mutation.error.message}</Styled.Error>}
        <Button type="submit" size="large" isLoader={mutation.isLoading}>
          Save Priorities
        </Button>
      </Styled.Form>
      <CopilotTestChat
        title="Test auto set priorities"
        description="When defined, Magic AI will automatically set the priority on the thread. You can define
          these on a channel level by adding it to a flow."
        firstMessage="Hi, I’m Magic AI! Ask me something or tell me your issue and I’ll give you the priority!"
        askAnotherMessage="Ask me another one and I’ll give you the priority!"
        onSend={handleSendMessage}
        newMessageNormalizer={newMessageNormalizer}
      />
    </Styled.Wrapper>
  );
};

CopilotPrioritization.propTypes = {
  companyId: PropTypes.number.isRequired
};

export default CopilotPrioritization;
